import { TimeSlotType } from 'fitbeat.models';
import { DateRange } from './date-range';

function isoDateOnly(date: Date) {
  return date.toISOString().substring(0, 10);
}

interface ISpecificTimeSlotType {
  range: DateRange;
  slotType: TimeSlotType;
}

export class SpecificOperatingTimes {
  private readonly specificTimeSlotTypes: ISpecificTimeSlotType[] = [];

  public get(day: Date): TimeSlotType | undefined {
    const firstMatch = this.specificTimeSlotTypes.find(
      (specific: ISpecificTimeSlotType) => specific.range.isDateInThisRange(day),
    );
    const slotType = firstMatch?.slotType;
    return slotType;
  }

  public setClosedOnDays(days: string[], specificTimeZone?: string) {
    const slotType: TimeSlotType = TimeSlotType.None;
    days.forEach((day) => this.setTimeSlot(day, slotType, specificTimeZone));
  }

  public setTimeSlot(yyyymmdd: string, slotType: TimeSlotType, specificTimeZone?: string) {
    const isMonthAndDayOnly = yyyymmdd.match(/^(0[1-9]|1[012])[-](0[1-9]|[12][0-9]|3[01])$/);
    if (isMonthAndDayOnly) {
      // this assumes the service is restarted at least once in 2 years
      const nowIsoString = isoDateOnly(new Date());
      const currentYear = parseInt(nowIsoString.substring(0, 4), 10);
      this.setTimeSlot(`${currentYear + 0}-${yyyymmdd}`, slotType, specificTimeZone);
      this.setTimeSlot(`${currentYear + 1}-${yyyymmdd}`, slotType, specificTimeZone);
      this.setTimeSlot(`${currentYear + 2}-${yyyymmdd}`, slotType, specificTimeZone);
      return;
    }

    const matchTimeRange = yyyymmdd.match(/^([0-9-]{10}T[0-9:]{8}) to ([0-9-]{10}T[0-9:]{8})$/);
    if (matchTimeRange && specificTimeZone) {
      this.setTimeSlotForRange(matchTimeRange[1], matchTimeRange[2], specificTimeZone, slotType);
    } else if (specificTimeZone) {
      const range = DateRange.wholeDayInSpecificTimezone(yyyymmdd, specificTimeZone);
      this.specificTimeSlotTypes.push({ range, slotType });
    } else {
      const range = DateRange.wholeDayInCurrentTimezone(yyyymmdd);
      this.specificTimeSlotTypes.push({ range, slotType });
    }
  }

  public setTimeSlotForRange(start: string, end: string, specificTimeZone: string, slotType: TimeSlotType) {
    const range = DateRange.rangeInSpecificTimezone(start, end, specificTimeZone);
    this.specificTimeSlotTypes.push({ range, slotType });
  }

  public reset() {
    while (this.specificTimeSlotTypes.pop()) { /* no-op */ }
  }

  public describe(): any {
    const now = new Date();
    const futureDates = this.specificTimeSlotTypes
      .filter((specific: ISpecificTimeSlotType) => specific.range.endsAfter(now))
      .map((specific: ISpecificTimeSlotType) => `${specific.range.describe()}: ${specific.slotType}`)
      .sort();

    return {
      futureDates,
    };
  }
}
